import type { TranslationMessages } from "ra-core";
import type { PartialCrmMessages } from "@/components/atomic-crm/providers/commons/englishCrmMessages";

/**
 * Catálogo en castellano de Nevora Sales.
 *
 * Cuatro bloques, que el i18nProvider fusiona SOBRE el inglés (lo que falte cae al
 * inglés, nunca a la clave):
 *  - mensajesRaEs:         claves `ra.*` de ra-core (ra-language-english 5.14.7).
 *  - mensajesRaSupabaseEs: claves `ra-supabase.*` (ra-supabase-language-english 3.5.0).
 *  - mensajesCrmEs:        claves `resources.*` y `crm.*` de Atomic que sí montamos.
 *  - mensajesNevoraEs:     claves `nevora.*` propias.
 *
 * Pluralización polyglot: "singular |||| plural" con %{smart_count}.
 */
export const mensajesRaEs: TranslationMessages = {
  ra: {
    action: {
      add_filter: "Añadir filtro",
      add: "Añadir",
      back: "Volver",
      bulk_actions:
        "1 elemento seleccionado |||| %{smart_count} elementos seleccionados",
      cancel: "Cancelar",
      clear_array_input: "Vaciar la lista",
      clear_input_value: "Borrar valor",
      clone: "Duplicar",
      confirm: "Confirmar",
      create: "Crear",
      create_item: "Crear %{item}",
      delete: "Borrar",
      edit: "Editar",
      export: "Exportar",
      list: "Lista",
      refresh: "Actualizar",
      remove_filter: "Quitar este filtro",
      remove_all_filters: "Quitar todos los filtros",
      remove: "Quitar",
      reset: "Restablecer",
      save: "Guardar",
      search: "Buscar",
      search_columns: "Buscar columnas",
      select_all: "Seleccionar todo",
      select_all_button: "Seleccionar todo",
      select_row: "Seleccionar esta fila",
      show: "Ver",
      sort: "Ordenar",
      undo: "Deshacer",
      unselect: "Deseleccionar",
      expand: "Desplegar",
      close: "Cerrar",
      open_menu: "Abrir menú",
      close_menu: "Cerrar menú",
      update: "Actualizar",
      move_up: "Subir",
      move_down: "Bajar",
      open: "Abrir",
      toggle_theme: "Cambiar modo claro/oscuro",
      select_columns: "Columnas",
      update_application: "Recargar la aplicación",
    },
    boolean: {
      true: "Sí",
      false: "No",
      null: " ",
    },
    page: {
      create: "Crear %{name}",
      dashboard: "Inicio",
      edit: "%{name} %{recordRepresentation}",
      error: "Algo ha ido mal",
      list: "%{name}",
      loading: "Cargando",
      not_found: "No encontrado",
      show: "%{name} %{recordRepresentation}",
      empty: "Todavía no hay %{name}.",
      invite: "¿Quieres añadir uno?",
      access_denied: "Acceso denegado",
      authentication_error: "Error de autenticación",
    },
    input: {
      file: {
        upload_several:
          "Arrastra archivos aquí o haz clic para seleccionarlos.",
        upload_single:
          "Arrastra un archivo aquí o haz clic para seleccionarlo.",
      },
      image: {
        upload_several:
          "Arrastra imágenes aquí o haz clic para seleccionarlas.",
        upload_single:
          "Arrastra una imagen aquí o haz clic para seleccionarla.",
      },
      references: {
        all_missing: "No se han encontrado los datos referenciados.",
        many_missing:
          "Al menos una de las referencias asociadas ya no está disponible.",
        single_missing: "La referencia asociada ya no está disponible.",
      },
      password: {
        toggle_visible: "Ocultar contraseña",
        toggle_hidden: "Mostrar contraseña",
      },
    },
    message: {
      about: "Acerca de",
      access_denied: "No tienes permiso para acceder a esta página",
      are_you_sure: "¿Seguro?",
      authentication_error:
        "El servidor de autenticación ha devuelto un error y no se han podido comprobar tus credenciales.",
      auth_error: "Ha ocurrido un error al validar el token de autenticación.",
      bulk_delete_content:
        "¿Seguro que quieres borrar este %{name}? |||| ¿Seguro que quieres borrar estos %{smart_count} elementos?",
      bulk_delete_title: "Borrar %{name} |||| Borrar %{smart_count} %{name}",
      bulk_update_content:
        "¿Seguro que quieres actualizar %{name} %{recordRepresentation}? |||| ¿Seguro que quieres actualizar estos %{smart_count} elementos?",
      bulk_update_title:
        "Actualizar %{name} %{recordRepresentation} |||| Actualizar %{smart_count} %{name}",
      clear_array_input: "¿Seguro que quieres vaciar toda la lista?",
      delete_content: "¿Seguro que quieres borrar este %{name}?",
      delete_title: "Borrar %{name} %{recordRepresentation}",
      details: "Detalles",
      error: "Ha ocurrido un error y no se ha podido completar la petición.",
      invalid_form: "El formulario tiene errores. Revísalo.",
      loading: "Un momento",
      no: "No",
      not_found: "La dirección no existe o el enlace está mal.",
      select_all_limit_reached:
        "Hay demasiados elementos para seleccionarlos todos. Solo se han seleccionado los primeros %{max}.",
      unsaved_changes:
        "Hay cambios sin guardar. ¿Seguro que quieres descartarlos?",
      yes: "Sí",
      placeholder_data_warning:
        "Problema de red: no se han podido actualizar los datos.",
    },
    navigation: {
      clear_filters: "Quitar filtros",
      no_filtered_results: "No hay %{name} con los filtros actuales.",
      no_results: "No hay %{name}",
      no_more_results:
        "La página %{page} está fuera de rango. Prueba la anterior.",
      page_out_of_boundaries: "Página %{page} fuera de rango",
      page_out_from_end: "No se puede pasar de la última página",
      page_out_from_begin: "No se puede ir antes de la página 1",
      page_range_info: "%{offsetBegin}-%{offsetEnd} de %{total}",
      partial_page_range_info:
        "%{offsetBegin}-%{offsetEnd} de más de %{offsetEnd}",
      current_page: "Página %{page}",
      page: "Ir a la página %{page}",
      first: "Primera página",
      last: "Última página",
      next: "Página siguiente",
      previous: "Página anterior",
      page_rows_per_page: "Filas por página:",
      skip_nav: "Saltar al contenido",
    },
    sort: {
      sort_by: "Ordenar por %{field_lower_first} %{order}",
      ASC: "ascendente",
      DESC: "descendente",
    },
    auth: {
      auth_check_error: "Inicia sesión para continuar",
      user_menu: "Perfil",
      username: "Usuario",
      password: "Contraseña",
      email: "Email",
      sign_in: "Iniciar sesión",
      sign_in_error: "No se ha podido iniciar sesión. Inténtalo de nuevo.",
      logout: "Cerrar sesión",
    },
    notification: {
      updated:
        "Elemento actualizado |||| %{smart_count} elementos actualizados",
      created: "Elemento creado",
      deleted: "Elemento borrado |||| %{smart_count} elementos borrados",
      bad_item: "Elemento incorrecto",
      item_doesnt_exist: "El elemento no existe",
      http_error: "Error de comunicación con el servidor",
      data_provider_error: "Error del proveedor de datos. Revisa la consola.",
      i18n_error: "No se han podido cargar las traducciones",
      canceled: "Acción cancelada",
      logged_out: "Tu sesión ha caducado. Vuelve a iniciar sesión.",
      not_authorized: "No tienes autorización para acceder a este recurso.",
      application_update_available: "Hay una versión nueva disponible.",
      offline: "Sin conexión. No se han podido cargar los datos.",
    },
    validation: {
      required: "Obligatorio",
      minLength: "Debe tener al menos %{min} caracteres",
      maxLength: "Debe tener como máximo %{max} caracteres",
      minValue: "Debe ser al menos %{min}",
      maxValue: "Debe ser como máximo %{max}",
      number: "Debe ser un número",
      email: "Debe ser un email válido",
      oneOf: "Debe ser uno de: %{options}",
      regex: "Debe cumplir el formato (expresión regular): %{pattern}",
      unique: "Debe ser único",
    },
    saved_queries: {
      label: "Consultas guardadas",
      query_name: "Nombre de la consulta",
      new_label: "Guardar la consulta actual…",
      new_dialog_title: "Guardar la consulta actual como",
      remove_label: "Quitar consulta guardada",
      remove_label_with_name: "Quitar la consulta «%{name}»",
      remove_dialog_title: "¿Quitar la consulta guardada?",
      remove_message: "¿Seguro que quieres quitar esta consulta de tu lista?",
      help: "Filtra la lista y guarda la consulta para más adelante",
    },
    guesser: {
      empty: {
        title: "No hay datos que mostrar",
        message: "Revisa el proveedor de datos",
      },
    },
    configurable: {
      customize: "Personalizar",
      configureMode: "Configurar esta página",
      inspector: {
        title: "Inspector",
        content:
          "Pasa el ratón por los elementos de la interfaz para configurarlos",
        reset: "Restablecer ajustes",
        hideAll: "Ocultar todo",
        showAll: "Mostrar todo",
      },
      Datagrid: {
        title: "Tabla",
        unlabeled: "Columna sin etiqueta n.º %{column}",
      },
      SimpleForm: {
        title: "Formulario",
        unlabeled: "Campo sin etiqueta n.º %{input}",
      },
      SimpleList: {
        title: "Lista",
        primaryText: "Texto principal",
        secondaryText: "Texto secundario",
        tertiaryText: "Texto terciario",
      },
    },
  },
};

export const mensajesRaSupabaseEs = {
  "ra-supabase": {
    auth: {
      email: "Email",
      confirm_password: "Confirmar contraseña",
      sign_in_with: "Entrar con %{provider}",
      forgot_password: "¿Has olvidado tu contraseña?",
      reset_password: "Restablecer contraseña",
      password_reset:
        "Si tu email está registrado, recibirás un mensaje con un enlace para crear una contraseña nueva.",
      missing_tokens: "Faltan los tokens de acceso y de refresco",
      back_to_login: "Volver al inicio de sesión",
    },
    reset_password: {
      forgot_password: "¿Has olvidado tu contraseña?",
      forgot_password_details:
        "Escribe tu email y te enviaremos las instrucciones.",
    },
    set_password: {
      new_password: "Elige tu contraseña",
    },
    validation: {
      password_mismatch: "Las contraseñas no coinciden",
    },
  },
};

/** Claves de Atomic que sí se usan en Nevora Sales (usuarios, acceso, perfil, tema). */
export const mensajesCrmEs: PartialCrmMessages = {
  resources: {
    sales: {
      name: "Usuario |||| Usuarios",
      fields: {
        first_name: "Nombre",
        last_name: "Apellidos",
        email: "Email",
        secondary_email: "Email secundario",
        secondary_emails: "Emails secundarios",
        administrator: "Admin",
        disabled: "Desactivado",
      },
      create: {
        error: "Ha ocurrido un error al crear el usuario.",
        success: "Usuario creado. Recibirá un email para crear su contraseña.",
        title: "Crear usuario",
      },
      edit: {
        error: "Ha ocurrido un error. Inténtalo de nuevo.",
        record_not_found: "Usuario no encontrado",
        success: "Usuario actualizado",
        title: "Editar %{name}",
      },
      action: {
        new: "Nuevo usuario",
      },
    },
  },
  crm: {
    action: {
      reset_password: "Restablecer contraseña",
    },
    auth: {
      first_name: "Nombre",
      last_name: "Apellidos",
      confirm_password: "Confirmar contraseña",
      confirmation_required:
        "Sigue el enlace que te acabamos de enviar por email para confirmar tu cuenta.",
      recovery_email_sent:
        "Si tu email está registrado, recibirás en breve un mensaje para recuperar la contraseña.",
      sign_in_failed: "No se ha podido iniciar sesión.",
      sign_in_google_workspace: "Entrar con Google Workspace",
      signup: {
        create_account: "Crear cuenta",
        create_first_user:
          "Crea la primera cuenta de usuario para completar la configuración.",
        creating: "Creando…",
        initial_user_created: "Primer usuario creado correctamente",
      },
      welcome_title: "Bienvenido a Nevora CRM",
    },
    common: {
      account_manager: "Responsable",
      activity: "Actividad",
      added: "añadido",
      details: "Detalles",
      last_activity_with_date: "última actividad %{date}",
      load_more: "Cargar más",
      misc: "Otros",
      past: "Pasado",
      read_more: "Leer más",
      retry: "Reintentar",
      show_less: "Mostrar menos",
      copied: "¡Copiado!",
      copy: "Copiar",
      loading: "Cargando…",
      me: "Yo",
      task_count: "%{smart_count} tarea |||| %{smart_count} tareas",
    },
    changelog: {
      title: "Novedades",
    },
    settings: {
      about: "Acerca de",
      preferences: "Preferencias",
      title: "Ajustes",
      saved: "Configuración guardada",
      save_error: "No se ha podido guardar la configuración",
      saving: "Guardando…",
    },
    theme: {
      dark: "Oscuro",
      label: "Tema",
      light: "Claro",
      system: "Sistema",
    },
    language: "Idioma",
    navigation: {
      label: "Navegación de Nevora CRM",
    },
    profile: {
      password: {
        change: "Cambiar contraseña",
      },
      password_reset_sent:
        "Te hemos enviado un email para restablecer la contraseña",
      record_not_found: "Usuario no encontrado",
      title: "Perfil",
      updated: "Tu perfil se ha actualizado",
      update_error: "Ha ocurrido un error. Inténtalo de nuevo",
    },
    validation: {
      invalid_url: "Debe ser una URL válida",
      invalid_linkedin_url: "La URL debe ser de linkedin.com",
    },
  },
};

/** Claves propias de Nevora Sales. */
export const mensajesNevoraEs = {
  nevora: {
    app: {
      nombre: "Nevora CRM",
      descriptor: "Sistematizamos tu operativa con IA.",
      crm_interno: "CRM interno de Nevora Consulting",
    },
    nav: {
      inicio: "Inicio",
      leads: "Leads",
      pipeline: "Pipeline",
      agenda: "Agenda",
      perfil: "Perfil",
      usuarios: "Usuarios",
      ajustes: "Ajustes",
    },
    login: {
      titulo: "Iniciar sesión",
      entrar: "Entrar",
      olvido: "¿Has olvidado tu contraseña?",
      mostrar_contrasena: "Mostrar contraseña",
      ocultar_contrasena: "Ocultar contraseña",
      enlace_no_valido: "Este enlace ya no sirve: ya se usó o ha caducado.",
      enlace_no_valido_ayuda:
        "Cada enlace de los correos sirve una sola vez y caduca en una hora. Escribe tu email y te enviamos uno nuevo. Si era una invitación y no te llega, pide a Jan o a Robert que te la reenvíen.",
    },
    registro: {
      titulo: "Bienvenido a Nevora CRM",
      texto:
        "Crea la primera cuenta de administrador para completar la configuración. Solo se hace una vez.",
      crear: "Crear cuenta de administrador",
      creando: "Creando…",
      creado: "Cuenta de administrador creada",
      error_login:
        "La cuenta se ha creado pero no se ha podido iniciar sesión.",
    },
    panel: {
      hola: "Hola, %{nombre}",
      en_construccion: "Nevora CRM está en construcción.",
      fase_actual: "Fase 1: base, acceso y marca.",
      proximo:
        "En la fase 2 llegan los leads, la ficha de negocio, el pipeline, la agenda y el panel de dirección.",
      rol_admin: "Tu cuenta es de administrador.",
      rol_comercial: "Tu cuenta es de comercial.",
    },
    perfil: {
      titulo: "Perfil",
      nombre: "Nombre",
      email: "Email",
      rol: "Rol",
      admin: "Administrador",
      comercial: "Comercial",
      tema: "Tema",
      cerrar_sesion: "Cerrar sesión",
      cambiar_contrasena_ayuda:
        "Para cambiar la contraseña, cierra sesión y usa «¿Has olvidado tu contraseña?» en la pantalla de entrada.",
    },
  },
};
